import { useCallback } from 'react';
import { ENDPOINTS, type EndpointKey } from '@/generated/endpoints';

import { matchAll } from './match';
import type { RequiredScopeParams } from './types';
import { usePermissionsIndex } from './use-permissions-index';

/**
 * Client-side permission checks against the cached /CurrentUserPermissions Set.
 *
 * TypeScript enforces required scope params per endpoint: forgetting to pass
 * `organization`/`workspace` when a path needs it will result in a compile error.
 *
 * Assumes the caller renders inside `ProtectedRoute` (or otherwise after
 * auth is confirmed). If you need to call this from outside that subtree,
 * pass `enabled: false` where the query must not run; `hasPermission` still
 * reads whatever is cached.
 *
 * Usage:
 *   const { hasPermission } = usePermissions();
 *   const canPublish = hasPermission(['PublishAutomation'], {
 *     organization: orgId,
 *     workspace: workspaceId,
 *   });
 */
export function usePermissions({ enabled = true }: { enabled?: boolean } = {}) {
  const { data: index, isLoading } = usePermissionsIndex({ enabled });

  const hasPermission = useCallback(
    <const K extends EndpointKey>(
      keys: readonly K[],
      ...rest: [RequiredScopeParams<K>] extends [never]
        ? [params?: Readonly<Record<string, string>>]
        : [params: Readonly<Record<RequiredScopeParams<K>, string>>]
    ): boolean => {
      if (!index) return false;
      const params = (rest[0] ?? {}) as Readonly<Record<string, string>>;
      return matchAll(
        keys.map(k => ENDPOINTS[k]),
        params,
        index,
      );
    },
    [index],
  );

  return { hasPermission, isLoading };
}
